import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FiCheckCircle, FiClock, FiFilter, FiX } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { activitiesAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';

function itemTitle(item) {
  if (item.prospect_id) return `${item.prospect_ref || ''} ${item.prospect_name || ''}`.trim() || `Prospect #${item.prospect_id}`;
  if (item.student_id) return `${item.student_ref || ''} ${item.student_name || ''}`.trim() || `Student #${item.student_id}`;
  if (item.lead_id) return `${item.lead_ref || ''} ${item.lead_name || ''}`.trim() || `Lead #${item.lead_id}`;
  return `Activity #${item.item_id}`;
}

function itemEntity(item) {
  if (item.prospect_id) return 'prospect';
  if (item.student_id) return 'student';
  if (item.lead_id) return 'lead';
  return 'other';
}

function itemPath(item) {
  if (item.prospect_id) return `/prospects/${item.prospect_id}`;
  if (item.student_id) return `/students/${item.student_id}`;
  if (item.lead_id) return `/b2b-agencies/${item.lead_id}`;
  return '';
}

function isOverdueDate(nextFollowup) {
  if (!nextFollowup) return false;
  const dueDay = String(nextFollowup).slice(0, 10);
  const today = new Date(new Date().toLocaleString('en-US', { timeZone: 'Asia/Kolkata' }));
  const todayStr = today.toISOString().slice(0, 10);
  return dueDay < todayStr;
}

const baseBtn = {
  border: '1px solid var(--border)',
  borderRadius: 8,
  padding: '5px 9px',
  background: 'var(--white)',
  cursor: 'pointer',
  fontSize: 12,
  lineHeight: 1.1
};

const filterInput = {
  width: '100%',
  height: 32,
  border: '1px solid var(--border)',
  borderRadius: 8,
  background: 'var(--white)',
  fontSize: 12,
  padding: '0 8px'
};

export default function FollowupFloatingPanel() {
  const { hasModuleAccess } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [marking, setMarking] = useState({});
  const [filters, setFilters] = useState({
    scope: 'today_overdue',
    search: ''
  });

  const canReadActivities = hasModuleAccess('activities');

  const loadFollowups = useCallback(async () => {
    if (!canReadActivities) return;
    setLoading(true);
    try {
      const res = await activitiesAPI.getFollowups(filters);
      setItems(Array.isArray(res?.data?.items) ? res.data.items : []);
    } catch (_) {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [filters, canReadActivities]);

  useEffect(() => {
    if (!open) return;
    loadFollowups();
  }, [open, loadFollowups]);

  const activeFilterCount = useMemo(() => {
    let n = 0;
    if (filters.scope !== 'today_overdue') n += 1;
    if (String(filters.search || '').trim()) n += 1;
    return n;
  }, [filters]);

  const markDone = async (item) => {
    const key = `${item.source_table}:${item.item_id}`;
    setMarking((prev) => ({ ...prev, [key]: true }));
    try {
      await activitiesAPI.markFollowupDone(item.item_id, item.source_table);
      setItems((prev) => prev.filter((x) => !(x.source_table === item.source_table && x.item_id === item.item_id)));
    } catch (_) {
      // keep silent; page can refresh
    } finally {
      setMarking((prev) => ({ ...prev, [key]: false }));
    }
  };

  if (!canReadActivities) return null;

  return (
    <div style={{ position: 'fixed', right: 20, bottom: 20, zIndex: 1200 }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        style={{
          width: 56,
          height: 56,
          borderRadius: '50%',
          border: 'none',
          background: 'var(--primary)',
          color: '#fff',
          cursor: 'pointer',
          boxShadow: '0 12px 30px rgba(2,6,23,0.24)'
        }}
        title="Follow-ups"
      >
        <FiClock size={20} />
      </button>

      {open && (
        <div
          style={{
            position: 'absolute',
            right: 0,
            bottom: 68,
            width: 392,
            maxWidth: '90vw',
            maxHeight: '75vh',
            overflow: 'hidden',
            borderRadius: 12,
            border: '1px solid var(--border)',
            background: 'var(--white)',
            boxShadow: '0 16px 42px rgba(2,6,23,0.22)',
            display: 'grid',
            gridTemplateRows: 'auto auto 1fr'
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 10px', borderBottom: '1px solid var(--border)' }}>
            <div style={{ fontWeight: 700, fontSize: 14 }}>Follow-ups</div>
            <button type="button" onClick={() => setOpen(false)} style={{ ...baseBtn, padding: '4px 8px' }}><FiX /></button>
          </div>

          <div style={{ padding: 8, borderBottom: '1px solid var(--border)', display: 'grid', gap: 6 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
              <div style={{ fontSize: 12, fontWeight: 700 }}>Filters</div>
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                {activeFilterCount > 0 && (
                  <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{activeFilterCount} active</span>
                )}
                <button
                  type="button"
                  style={{ ...baseBtn, padding: '4px 8px' }}
                  onClick={() => setFilters({ scope: 'today_overdue', search: '' })}
                >
                  Reset
                </button>
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'minmax(120px, 155px) 1fr auto', gap: 6, alignItems: 'end' }}>
              <label style={{ display: 'grid', gap: 4, fontSize: 11, color: 'var(--text-muted)', margin: 0 }}>
                Scope
                <select style={filterInput} value={filters.scope} onChange={(e) => setFilters((f) => ({ ...f, scope: e.target.value }))}>
                <option value="today_overdue">Today + Overdue</option>
                <option value="today">Today</option>
                <option value="overdue">Overdue</option>
                <option value="all">All</option>
                </select>
              </label>
              <label style={{ display: 'grid', gap: 4, fontSize: 11, color: 'var(--text-muted)', margin: 0 }}>
              Search
              <input
                type="text"
                style={filterInput}
                placeholder="Search notes, name, ref"
                value={filters.search}
                onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))}
              />
            </label>
              <button type="button" onClick={loadFollowups} style={{ ...baseBtn, height: 32, whiteSpace: 'nowrap' }}>
                <FiFilter /> Apply
              </button>
            </div>
          </div>

          <div style={{ overflowY: 'auto', padding: 8, display: 'grid', gap: 6 }}>
            {loading ? (
              <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>Loading...</div>
            ) : items.length === 0 ? (
              <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>No pending follow-ups.</div>
            ) : (
              items.map((item) => {
                const key = `${item.source_table}:${item.item_id}`;
                const path = itemPath(item);
                const entity = itemEntity(item);
                const isOverdue = isOverdueDate(item.next_followup);
                return (
                  <div key={key} style={{ border: '1px solid var(--border)', borderRadius: 9, padding: 8 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: 12, color: 'var(--text-muted)', textTransform: 'uppercase' }}>{entity}</div>
                        <div style={{ fontWeight: 700, fontSize: 13 }}>{itemTitle(item)}</div>
                        <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                          {item.activity_type} • Due {String(item.next_followup || '').slice(0, 10)}
                        </div>
                        {item.notes ? <div style={{ marginTop: 4, fontSize: 12 }}>{item.notes}</div> : null}
                      </div>
                      <div style={{ display: 'grid', gap: 5, alignContent: 'start' }}>
                        {isOverdue ? (
                          <span style={{ fontSize: 11, color: 'var(--danger)', fontWeight: 700, textAlign: 'right' }}>
                            Overdue
                          </span>
                        ) : null}
                        <button type="button" style={baseBtn} disabled={!path} onClick={() => path && navigate(path)}>Open</button>
                        <button
                          type="button"
                          style={{ ...baseBtn, borderColor: '#86efac', background: '#ecfdf5', whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', gap: 6 }}
                          disabled={!!marking[key]}
                          onClick={() => markDone(item)}
                        >
                          <FiCheckCircle /> {marking[key] ? 'Saving...' : 'Mark done'}
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}

